/**
 * The chrome every screen renders inside — NIMER Pro identity (redesign
 * handoff §3).
 *
 * A 60px ink-950 header carries the white logo, a hairline divider, the mono
 * gold tagline, and — right-aligned — whatever mono segments the screen put in
 * `shellMeta`. Staff and admin planes add a 2px gold keyline under the header
 * (the plane marker); the admin plane adds its section nav strip. The page
 * ground is flat stone-50 — the blueprint grid retired with the old identity.
 *
 * The credential warning (S1) is not chrome: the entry screen shows it once at
 * first contact and the wizard shows a compact SectionNotice on any screen
 * whose chunk accepts free text (redesign spec §2, unchanged here).
 */
import type { ReactNode } from 'react'

import { LoadingOverlay } from '../loading/LoadingOverlay.js'

import './shell.css'
import './npro.css'
import './surfaces.css'
import { t } from './i18n.ts'
import { useShellMeta } from './shellMeta.ts'
import { EngagementSwitcher } from '../person/EngagementSwitcher.tsx'
import { useLocationPath } from '../app/navigation.ts'
import { useViewer } from '../app/viewer.ts'
import type { RoutePlane } from '../routes/_registry/types.ts'
import logoWhite from './assets/nimer-pro-logo-white.png'

export const MAIN_CONTENT_ID = 'main-content'

/** Which staff console this header's tagline names. Meaningless off the staff plane. */
export type StaffArea = 'consultant' | 'admin'

function taglineFor(plane: RoutePlane, staffArea: StaffArea | undefined): string {
  if (plane !== 'staff') return t('shell.tagline.customer')

  return staffArea === 'admin'
    ? t('shell.tagline.staff.admin')
    : t('shell.tagline.staff.consultant')
}

const ADMIN_NAV = [
  { path: '/admin', key: 'admin.nav.overview' },
  { path: '/admin/users', key: 'admin.nav.users' },
  { path: '/admin/packs', key: 'admin.nav.packs' },
  { path: '/admin/estimation', key: 'admin.nav.estimation' },
  { path: '/admin/audit', key: 'admin.nav.audit' },
  { path: '/staff/help', key: 'admin.nav.help' },
] as const

/** The admin section nav (mock 3A): links only, active = gold underline. */
function AdminNav({ path }: { readonly path: string }) {
  return (
    <nav className="shell__sections" aria-label={t('admin.nav.label')}>
      <div className="shell__sections-inner">
        {ADMIN_NAV.map((item) => (
          <a
            key={item.path}
            href={item.path}
            className="shell__section-link"
            aria-current={path === item.path ? 'page' : undefined}
          >
            {t(item.key)}
          </a>
        ))}
      </div>
    </nav>
  )
}

/**
 * Which engagement the customer is working on — a band of its own, under the
 * header (IMPL-ENGAGE-002 §8.3).
 *
 * ┌─ IT USED TO SIT IN THE DARK HEADER, BESIDE THE TAGLINE. ─────────────────┐
 * │ Sixty pixels of chrome shared with the logo, on the ink ground, is where │
 * │ a product puts things nobody needs to read. The engagement you are       │
 * │ answering FOR is not that: answer the right questions against the wrong  │
 * │ organisation and the work is wrong, not merely misplaced.                │
 * │                                                                          │
 * │ On the page ground, full width, first thing under the header — so it is  │
 * │ read before the screen is, on every customer screen including the        │
 * │ questionnaire, which is exactly where it matters most.                   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Gated on the viewer rather than the plane: `Shell` also wraps the OTP form
 * itself, where the plane is already `customer` but no session exists — and
 * asking `/api/v1/me/engagements` there would be a guaranteed 401 on every
 * render of the sign-in screen.
 */
function ContextBar() {
  if (useViewer().kind !== 'customer') return null

  return (
    <div className="shell__context">
      <div className="shell__context-inner">
        <EngagementSwitcher />
      </div>
    </div>
  )
}

function HeaderMeta() {
  const segments = useShellMeta()
  if (segments.length === 0) return null

  return (
    <div className="shell__header-meta">
      {segments.map((segment, index) => (
        <span key={segment} className="shell__header-meta-item">
          {index > 0 ? <span className="shell__header-divider" aria-hidden="true" /> : null}
          <span className="shell__meta-text">{segment}</span>
        </span>
      ))}
    </div>
  )
}

export function Shell({
  plane,
  staffArea,
  children,
}: {
  readonly plane: RoutePlane
  /** Which staff tree this render belongs to (spec §3). Ignored off the staff plane. */
  readonly staffArea?: StaffArea
  readonly children: ReactNode
}) {
  const path = useLocationPath()
  const keyline = plane === 'staff' ? ' shell__header--staff' : ''

  return (
    <div className="shell">
      <a className="shell__skip" href={`#${MAIN_CONTENT_ID}`}>
        {t('shell.skipToContent')}
      </a>
      <header className={`shell__header${keyline}`}>
        <div className="shell__header-inner">
          <div className="shell__brand">
            <img className="shell__logo" src={logoWhite} alt={t('app.shortName')} />
            <span className="shell__header-divider" aria-hidden="true" />
            <p className="shell__tagline">{taglineFor(plane, staffArea)}</p>
          </div>
          <div className="shell__header-right">
            <HeaderMeta />
          </div>
        </div>
      </header>
      {staffArea === 'admin' ? <AdminNav path={path} /> : null}
      <ContextBar />
      <main className="shell__main" id={MAIN_CONTENT_ID} tabIndex={-1}>
        {children}
      </main>
      <LoadingOverlay />
    </div>
  )
}
